/**
 * Support-services directory (Part 6 spec).
 *
 * A LOCAL, EDITABLE COPY of the same data seeded on the server.  We keep
 * a client-side copy so the "Get help now" page and the Amber/Red result
 * screens work without waiting on a network round-trip.  Numbers can be
 * edited by SuperAdmin in the DB (via the /api/support-directory endpoint)
 * and the client falls back to that live copy when it differs.
 *
 * Do NOT translate the numbers.  Do NOT translate 'aa.org' / 'na.org'.
 * Names of services are proper nouns — leave in English by default.
 */

export const UNIVERSAL_MEETINGS = [
  { name: 'Alcoholics Anonymous',
    url: 'https://aa.org',
    description: 'Find a meeting near you' },
  { name: 'Narcotics Anonymous',
    url: 'https://na.org',
    description: 'Find a meeting near you' },
];

export const FALLBACK_ENTRY = {
  country_code: null,
  country_name: 'Any other country',
  emergency_number: null,
  crisis_lines: [
    { name: 'findahelpline.com',
      url: 'https://findahelpline.com',
      notes: 'Find a crisis line in your country' },
  ],
};

export const SUPPORT_DIRECTORY = [
  { country_code: 'AU', country_name: 'Australia', emergency_number: '000',
    crisis_lines: [
      { name: 'Lifeline', number: '13 11 14', hours: '24/7', notes: 'Text 0477 13 11 14' },
      { name: 'Suicide Call Back Service', number: '1300 659 467', hours: '24/7' },
    ] },
  { country_code: 'NZ', country_name: 'New Zealand', emergency_number: '111',
    crisis_lines: [
      { name: '1737 Need to talk?', number: '1737', hours: '24/7', notes: 'Call or text 1737' },
      { name: 'Lifeline', number: '0800 543 354', hours: '24/7' },
    ] },
  { country_code: 'GB', country_name: 'United Kingdom', emergency_number: '999',
    crisis_lines: [ { name: 'Samaritans', number: '116 123', hours: '24/7' } ] },
  { country_code: 'IE', country_name: 'Ireland', emergency_number: '112 or 999',
    crisis_lines: [
      { name: 'Samaritans', number: '116 123', hours: '24/7' },
      { name: 'Pieta', number: '1800 247 247', hours: '24/7' },
    ] },
  { country_code: 'US', country_name: 'United States', emergency_number: '911',
    crisis_lines: [
      { name: '988 Suicide and Crisis Lifeline', number: '988', hours: '24/7', notes: 'Call or text 988' },
      { name: 'SAMHSA National Helpline', number: '1-800-662-4357', hours: '24/7' },
    ] },
  { country_code: 'CA', country_name: 'Canada', emergency_number: '911',
    crisis_lines: [
      { name: '9-8-8 Suicide Crisis Helpline', number: '988', hours: '24/7', notes: 'Call or text 988' },
    ] },
  { country_code: 'ES', country_name: 'Spain', emergency_number: '112',
    crisis_lines: [ { name: 'Línea 024', number: '024', hours: '24/7' } ] },
  { country_code: 'MX', country_name: 'Mexico', emergency_number: '911',
    crisis_lines: [ { name: 'Línea de la Vida', number: '800 911 2000', hours: '24/7' } ] },
  { country_code: 'IT', country_name: 'Italy', emergency_number: '112',
    crisis_lines: [
      { name: 'Telefono Amico', number: '02 2327 2327' },
      { name: 'De Leo Fund', number: '800 168 678' },
    ] },
  { country_code: 'PT', country_name: 'Portugal', emergency_number: '112',
    crisis_lines: [
      { name: 'SNS 24 Suicide Prevention and Psychological Support Line', number: '1411' },
    ] },
  { country_code: 'BR', country_name: 'Brazil', emergency_number: '192',
    crisis_lines: [ { name: 'CVV', number: '188', hours: '24/7' } ] },
  { country_code: 'RU', country_name: 'Russia', emergency_number: '112',
    crisis_lines: [
      { name: 'EMERCOM Emergency Psychological Assistance', number: '+7 495 989-50-50', hours: '24/7' },
    ] },
  { country_code: 'UA', country_name: 'Ukraine', emergency_number: '112',
    crisis_lines: [ { name: 'Lifeline Ukraine', number: '7333', hours: '24/7' } ] },
  { country_code: 'KR', country_name: 'South Korea', emergency_number: '119',
    crisis_lines: [
      { name: 'Suicide Prevention Hotline', number: '109' },
      { name: 'Lifeline Korea', number: '1588-9191', hours: '24/7' },
    ] },
  { country_code: 'CN', country_name: 'China', emergency_number: '120',
    crisis_lines: [
      { name: 'National Psychological Support Hotline', number: '12356' },
      { name: 'Hope Line', number: '400-161-9995' },
    ] },
  { country_code: 'TW', country_name: 'Taiwan', emergency_number: '119',
    crisis_lines: [
      { name: '1925 Care Hotline', number: '1925', hours: '24/7' },
      { name: 'Lifeline', number: '1995' },
    ] },
  { country_code: 'FR', country_name: 'France', emergency_number: '112',
    crisis_lines: [
      { name: '3114 Suicide Prevention', number: '3114', hours: '24/7' },
      { name: 'SOS Amitié', number: '09 72 39 40 50', hours: '24/7' },
    ] },
  { country_code: 'ZA', country_name: 'South Africa', emergency_number: '10111',
    crisis_lines: [
      { name: 'SADAG Suicide Crisis Line', number: '0800 567 567', hours: '24/7' },
      { name: 'Lifeline South Africa', number: '0861 322 322', hours: '24/7' },
    ] },
];

export function findCountry(code) {
  if (!code) return null;
  const cc = String(code).trim().toUpperCase();
  return SUPPORT_DIRECTORY.find(c => c.country_code === cc) || null;
}

/** Strip spaces, dashes and dots so `tel:` links dial reliably. */
export function telHref(number) {
  if (!number) return '';
  return 'tel:' + String(number).replace(/[\s\-.()]/g, '');
}
